import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Target, Cpu, Wrench } from "lucide-react";
import Layout from "@/components/Layout";
import { useI18n } from "@/i18n";
import { getVehicleBySlug } from "@/data/vehicles";
import { services } from "@/data/services";
import { stock } from "@/data/brand";

export default function VehicleDetail() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const v = getVehicleBySlug(slug);
  if (!v) return <Navigate to="/vehicles" replace />;

  const available = services
    .filter((s) => ["performance", "programming", "transmission"].includes(s.category))
    .slice(0, 8);

  return (
    <Layout seoTitle={`${v.name} — ECU`} seoDescription={`${v.name} ECU calibration, engines & tuning at MAD TURBO`}>
      {/* Hero */}
      <section className="relative pt-14 pb-14 md:pt-20 md:pb-20 border-b border-white/[0.06] overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={stock.engineBay} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-[0.45]" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black" />
        </div>
        <div className="container-mt relative z-10">
          <div className="flex items-center gap-4 mb-8">
            <Link to="/vehicles" className="inline-flex items-center gap-2 text-zinc-400 hover:text-brand-lime text-[11px] font-mono uppercase tracking-[0.22em] transition-colors">
              <ArrowLeft size={13} /> {t("common.backToVehicles")}
            </Link>
            <span className="h-px w-8 bg-zinc-800" />
            <span className="tech-chip tech-chip--lime">{t("labels.vehicleDatabase")}</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <h1 className="h-display text-6xl md:text-8xl lg:text-9xl text-white leading-none">{v.name}</h1>
            <div className="flex gap-6 md:gap-8">
              <div>
                <p className="eyebrow mb-1">{t("labels.engines")}</p>
                <p className="font-mono text-3xl md:text-4xl text-brand-lime tabular-nums">{v.engines.length}</p>
              </div>
              <div className="w-px bg-zinc-800" />
              <div>
                <p className="eyebrow mb-1">{t("labels.ecus")}</p>
                <p className="font-mono text-3xl md:text-4xl text-brand-lime tabular-nums">{v.ecus.length}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="section-y">
        <div className="container-mt grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-2 space-y-12">
            <div>
              <p className="kicker mb-5"><span className="kicker__bar" /><span>{t("vehicles.popularEngines")}</span></p>
              <div className="flex flex-wrap gap-2">
                {v.engines.map((e) => (
                  <span key={e} className="tech-chip tech-chip--lime">{e}</span>
                ))}
              </div>
            </div>

            <div>
              <p className="kicker mb-5"><span className="kicker__bar" /><span>{t("vehicles.supportedEcus")}</span></p>
              <div className="flex flex-wrap gap-2">
                {v.ecus.map((e) => (
                  <span key={e} className="tech-chip">{e}</span>
                ))}
              </div>
            </div>

            <div>
              <p className="kicker mb-5"><span className="kicker__bar" /><span>{t("vehicles.commonUpgrades")}</span></p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                {(lang === "lt" ? v.upgrades.lt : v.upgrades.en).map((u, i) => (
                  <li key={i} className="card-mt card-mt-pad !p-4 text-sm text-zinc-200 flex items-center gap-3">
                    <Wrench size={14} className="text-brand-lime shrink-0" />
                    {u}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="kicker mb-5"><span className="kicker__bar" /><span>{t("vehicles.availableServices")}</span></p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                {available.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    className="card-mt card-mt-pad !p-4 card-interactive group flex items-center justify-between gap-3"
                  >
                    <span className="text-sm text-white">{lang === "lt" ? s.title.lt : s.title.en}</span>
                    <ArrowRight size={14} className="text-brand-lime shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-28">
            <div className="card-mt card-mt-pad">
              <div className="icon-tile mb-5"><Target size={18} /></div>
              <p className="kicker mb-3"><span>MAD TURBO</span></p>
              <h2 className="h-display text-2xl md:text-3xl text-white mb-3">{t("common.ctaTitle")}</h2>
              <p className="text-zinc-400 text-sm mb-6 leading-relaxed">{t("common.ctaText")}</p>
              <Link to="/contact" className="btn-primary w-full justify-center">
                {t("common.bookConsultation")} <ArrowRight size={14} />
              </Link>
              <div className="divider-mt my-6" />
              <p className="eyebrow mb-3">Platform</p>
              <span className="tech-chip tech-chip--lime">{v.name}</span>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}
